import { DICTIONARY, REST } from './signs';
import type { MotionProvenance, SignEntry } from './types';

export interface MotionSegment {
  /** What this segment is called on screen, e.g. `PRO-1` or the letter `J`. */
  label: string;
  /** Null when nothing in the dictionary covers it. */
  sign: SignEntry | null;
}

export interface MotionPlan {
  segments: MotionSegment[];
  provenance: MotionProvenance;
  /** Segments that have motion, for splitting the token's time budget. */
  playable: number;
}

const BY_ID = new Map<string, SignEntry>(DICTIONARY.signs.map((sign) => [sign.id, sign]));

export function lookupSign(id: string): SignEntry | null {
  return BY_ID.get(id.toUpperCase()) ?? null;
}

export function lookupLetter(letter: string): SignEntry | null {
  return DICTIONARY.alphabet[letter.toUpperCase()] ?? null;
}

/** The letters the manual alphabet can actually perform, sorted. */
export function availableLetters(): string[] {
  return Object.keys(DICTIONARY.alphabet).sort();
}

/**
 * Turn a gloss token into a motion plan.
 *
 * Handles the notation the gloss contract uses: `fs-WORD` fingerspells,
 * `SIGN++` repeats, `CL:` marks a classifier, and a sign phrase like
 * `PRO-1 GO SHOP` plays its parts in order. Anything with no sourced motion
 * comes back as a null segment rather than a guess.
 */
export function planToken(token: string): MotionPlan {
  const trimmed = token.trim().toUpperCase();
  if (!trimmed) return { segments: [{ label: '', sign: REST }], provenance: { kind: 'none' }, playable: 1 };

  const repeats = trimmed.endsWith('++') ? 2 : 1;
  const base = repeats > 1 ? trimmed.slice(0, -2).trim() : trimmed;

  if (base.startsWith('FS-')) return fingerspell(base.slice(3), repeats);

  const words = base.split(/\s+/).filter(Boolean);
  const segments: MotionSegment[] = [];
  for (let pass = 0; pass < repeats; pass += 1) {
    for (const word of words) {
      // A classifier is a described shape, not a lexical sign — no motion here.
      // A bare single letter has no other reading, so it falls back to the
      // manual alphabet even without the `fs-` marker.
      const sign = word.startsWith('CL:')
        ? null
        : (lookupSign(word) ?? (word.length === 1 ? lookupLetter(word) : null));
      segments.push({ label: word, sign });
    }
  }

  return withProvenance(segments, null);
}

function fingerspell(word: string, repeats: number): MotionPlan {
  const letters = [...word].filter((character) => /[A-Z]/.test(character));
  const segments: MotionSegment[] = [];
  const unavailable: string[] = [];

  for (let pass = 0; pass < repeats; pass += 1) {
    for (const letter of letters) {
      const sign = lookupLetter(letter);
      if (!sign && !unavailable.includes(letter)) unavailable.push(letter);
      segments.push({ label: letter, sign });
    }
  }

  if (!segments.length) {
    return { segments: [{ label: word, sign: null }], provenance: { kind: 'none' }, playable: 0 };
  }
  return withProvenance(segments, unavailable);
}

function withProvenance(segments: MotionSegment[], unavailable: string[] | null): MotionPlan {
  const playable = segments.filter((segment) => segment.sign).length;

  let provenance: MotionProvenance;
  if (unavailable) {
    provenance = { kind: 'fingerspelled', unavailable };
  } else if (playable === 0) {
    provenance = { kind: 'none' };
  } else if (playable === segments.length && segments.every((s) => s.sign?.reviewed)) {
    provenance = { kind: 'reviewed' };
  } else {
    provenance = { kind: 'unreviewed' };
  }

  return { segments, provenance, playable };
}

/** One-line, honest summary of what the stage is showing. */
export function describeProvenance(provenance: MotionProvenance): string {
  switch (provenance.kind) {
    case 'reviewed':
      return 'Reviewed motion';
    case 'unreviewed':
      return 'Unverified motion';
    case 'fingerspelled':
      return provenance.unavailable.length
        ? `Fingerspelled · no motion for ${provenance.unavailable.join(' ')}`
        : 'Fingerspelled';
    case 'none':
      return 'No motion data';
  }
}
